import React, { useState } from "react";
import CcButton from "../../CoilConfigurator/components/CcButton";
import WarningBanner from "../components/WarningBanner";
import InputSpecificationSummary from "../components/InputSpecificationSummary";
import PipeGeometryTiles from "../components/PipeGeometryTiles";
import StepDownThresholdGauge from "../components/StepDownThresholdGauge";
import MatchedCatalogueDetails from "../components/MatchedCatalogueDetails";
import type { CadCheck, ConfigurationResult, EquipmentKind } from "../models/types";

interface EquipmentDetailViewProps {
  result: ConfigurationResult;
  equipmentKind: EquipmentKind;
  onBack: () => void;
  onToggleCad: (checks: CadCheck[]) => void;
}

const EquipmentDetailView = ({
  result,
  equipmentKind,
  onBack,
  onToggleCad,
}: EquipmentDetailViewProps) => {
  const equipment = equipmentKind === "blast-frame" ? result.blastFrame : result.inductionCoil;
  const [showCalcs, setShowCalcs] = useState(false);

  if (!equipment) {
    return (
      <section className="op-form-card">
        <p>
          Configuration cannot be automatically determined because required engineering data is
          unavailable.
        </p>
        <CcButton variant="secondary" onClick={onBack}>
          Back
        </CcButton>
      </section>
    );
  }

  const title = equipmentKind === "blast-frame" ? "Blast Frame" : "Induction Coil";

  return (
    <div className="op-detail">
      <CcButton variant="link" onClick={onBack}>
        ← Back to results
      </CcButton>

      <header className="op-detail__summary">
        <div>
          <p className="op-kicker">{title}</p>
          <h2 className="op-section-title">{equipment.configurationId}</h2>
        </div>
      </header>

      <InputSpecificationSummary
        project={result.project}
        pipe={result.pipe}
        process={result.process}
        inputSummary={result.inputSummary}
        versions={result.versions}
      />

      <PipeGeometryTiles
        pipe={result.pipe}
        process={result.process}
        inputSummary={result.inputSummary}
      />

      <StepDownThresholdGauge
        pipe={result.pipe}
        process={result.process}
        inputSummary={result.inputSummary}
      />

      {equipment.warnings.map((warning) => (
        <WarningBanner key={warning.title} warning={warning} />
      ))}

      <section className="op-form-card">
        <h3>Required Features</h3>
        <ul className="op-feature-list">
          {equipment.features
            .filter((f) => f.required)
            .map((feature) => (
              <li key={feature.name}>
                ✓ {feature.name}
                <span className="op-muted"> Required</span>
              </li>
            ))}
        </ul>
      </section>

      <section className="op-form-card">
        <h3>Equipment Options</h3>
        <table className="op-simple-table">
          <thead>
            <tr>
              <th>Option</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {equipment.options.map((option) => (
              <tr key={option.name}>
                <td>{option.name}</td>
                <td>{option.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="op-form-card">
        <h3>Engineering Calculations</h3>
        <dl className="op-summary-grid">
          {equipment.calculations.map((item) => (
            <div key={item.label}>
              <dt>{item.label}</dt>
              <dd>{item.value}</dd>
            </div>
          ))}
        </dl>
        <CcButton variant="link" onClick={() => setShowCalcs((v) => !v)}>
          {showCalcs ? "Hide calculation details" : "Show calculation details"}
        </CcButton>
        {showCalcs ? (
          <div className="op-calc-details">
            {equipment.decisions.map((decision) => (
              <div key={decision.question} className="op-decision">
                <h4>{decision.question}</h4>
                <p>
                  <strong>Input:</strong> {decision.input}
                </p>
                <p>
                  <strong>Rule:</strong> {decision.rule}
                </p>
                <p>
                  <strong>Result:</strong> {decision.result}
                </p>
                <p>
                  <strong>Rule Version:</strong> {decision.ruleVersion}
                </p>
              </div>
            ))}

            <div className="op-matched-catalogue-section">
              <h4>Matched catalogue</h4>
              <p className="op-muted">
                Catalogue rows evaluated for this configuration. Matched rows are the ones the rules
                selected.
              </p>
              <MatchedCatalogueDetails
                matchedCatalogue={result.matchedCatalogue}
                equipmentKind={equipmentKind}
              />
            </div>
          </div>
        ) : null}
      </section>

      {equipment.cadChecks.length > 0 ? (
        <section className="op-form-card">
          <h3>CAD Confirmation Required</h3>
          {equipment.cadChecks.map((check) => (
            <label key={check.id} className="op-check">
              <input
                type="checkbox"
                checked={check.confirmed}
                onChange={(event) =>
                  onToggleCad(
                    equipment.cadChecks.map((item) =>
                      item.id === check.id ? { ...item, confirmed: event.target.checked } : item,
                    ),
                  )
                }
              />
              {check.label}
            </label>
          ))}
          <p className="op-muted">Status: CAD CHECK REQUIRED</p>
        </section>
      ) : null}
    </div>
  );
};

export default EquipmentDetailView;
